import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { useFeatureFlagEnabled } from '@posthog/react';
import { ReactNode, useId, useRef, useState } from 'react';
import { ArrowLeft } from '../../../assets/icons/ArrowLeft';
import { CircleTickIcon } from '../../../assets/icons/CircleTickIcon';
import { GiftIcon } from '../../../assets/icons/GiftIcon';
import { ShieldTickIcon } from '../../../assets/icons/ShieldTick';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useConfirmation } from '../../../store/selectors/useConfirmation';
import { useQuote } from '../../../store/selectors/useQuote';
import { getDiscountDisplayData } from '../../../utils/discounts/getDiscountDisplayData';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { CallUsModal } from '../components/CallUsModal';
import { QuoteRadioButton } from '../components/QuoteRadioButton';
import { useQuotePageActions } from '../hooks/useQuotePageActions';
import { QuoteCoverOption } from '../QuotePage.types';
import { getPersonalisedQuoteContent } from './content/getPersonalisedQuoteContent';
import { getPersonalisedQuotePricing } from './content/getPersonalisedQuotePricing';
import { personalisedQuoteCopy as copy } from './content/personalisedQuoteContent';
import { currencyFormatter, wholeDollarFormatter } from '@domgen/shared/utils/currency/formatters';
import { PersonalisedUpsellModal } from './PersonalisedUpsellModal';
import { getTypography } from './typography';

const DEFAULT_WARRANTY_MONTHS = 12;

const divider = 'shadow-[inset_0_1px_0_var(--color-border-neutral)]';

const typography = getTypography(import.meta.env.VITE_BRAND_CODE);

const styles = {
  container: 'flex-1 flex flex-col items-center bg-bg-main tablet:px-space-500 tablet:py-space-700',
  panel:
    'flex flex-col gap-space-500 w-full px-space-500 py-space-700 bg-bg-main-container tablet:max-w-198 tablet:p-space-500 tablet:rounded-radius-200 tablet:border-t-4 tablet:border-brand-primary tablet:shadow-[0_6px_16px_rgba(0,0,0,0.12)]',
  header: 'flex flex-col gap-space-400',
  heading: `${typography.pageTitle} text-text-headline text-balance`,
  subheading: `${typography.lead} text-text-body`,
  options: 'flex flex-col gap-space-300',
  optionsHeading: `${typography.label} text-text-headline`,
  optionsHeadingError: `${typography.label} text-text-error`,
  cards: 'flex flex-col gap-space-500',
  card: 'flex flex-col p-space-500 rounded-radius-200 ring-inset cursor-pointer transition-colors',
  warrantyCard: 'gap-space-500',
  applianceCareCard: 'gap-space-300',
  cardDefault: 'ring-1 ring-neutral-500 bg-bg-main-container',
  cardSelected: 'ring-2 ring-neutral-900 bg-primary-50',
  cardError: 'ring-2 ring-input-border-error bg-bg-main-container',
  cardTitleRow: 'flex items-center gap-space-200',
  cardHeader:
    'flex flex-col gap-space-300 tablet:flex-row tablet:items-start tablet:justify-between',
  cardHeaderTitle: 'flex items-start gap-space-200',
  cardHeaderText: 'flex flex-col gap-space-200 tablet:gap-space-100',
  cardTitle: `${typography.cardTitle} text-text-headline`,
  cardSubtitle: `${typography.bodyStrong} text-text-body`,
  indented: 'pl-8',
  tagWrapper: 'pl-8 tablet:pl-0 tablet:shrink-0',
  tag: `flex items-center gap-space-200 py-space-200 px-space-400 rounded-radius-200 ring-1 ring-inset ring-text-success bg-bg-success ${typography.bodyStrong} text-text-success`,
  tagIcon: 'shrink-0',
  cardSection: `flex flex-col gap-space-300 pt-space-300 ${divider}`,
  warrantyBox: `py-space-200 px-space-400 rounded-radius-200 ring-1 ring-inset ring-primary-300 ${typography.body} text-text-body`,
  warrantyBoxDefault: 'bg-bg-primary',
  warrantyBoxSelected: 'bg-bg-main-container',
  applianceCareBody: 'flex flex-col gap-space-500',
  benefitsList: `flex flex-col gap-space-400 pt-space-500 list-none ${divider}`,
  benefit: 'flex flex-row gap-space-200 items-start',
  benefitIcon: 'text-accent-3-500 shrink-0 w-6 h-6 [&>svg]:w-6 [&>svg]:h-6',
  benefitText: 'flex flex-col gap-space-100',
  benefitTitle: `${typography.bodyStrong} text-text-body`,
  benefitDescription: `${typography.body} text-text-body`,
  pricing: `flex flex-col gap-space-300 pt-space-300 ${divider}`,
  promo: `${typography.promo} text-text-success`,
  price: `${typography.price} text-text-headline`,
  priceBeforeDiscount: 'line-through mr-space-200',
  trust: `flex flex-row items-start gap-space-200 py-space-200 px-space-300 rounded-radius-200 ring-1 ring-inset ring-[#038AD3] ${typography.body} text-text-body tablet:items-center`,
  trustIcon: 'text-[#038AD3] shrink-0 w-6 h-6 [&>svg]:w-6 [&>svg]:h-6',
  footer: 'flex flex-col gap-space-700',
  limitations: `${typography.body} text-text-body`,
  link: typography.bodyLinkStrong,
  actionRow:
    'flex flex-col-reverse items-center gap-space-500 tablet:flex-row tablet:justify-between tablet:h-12',
  buttonWrapper: `w-full tablet:w-fit flex items-center justify-center ${typography.button}`,
};

const BenefitIcon = () => (
  <span className={styles.benefitIcon} aria-hidden="true">
    <CircleTickIcon />
  </span>
);

const Benefit = ({ title, description }: { title: ReactNode; description: string }) => (
  <li className={styles.benefit}>
    <BenefitIcon />
    <div className={styles.benefitText}>
      <p className={styles.benefitTitle}>{title}</p>
      <p className={styles.benefitDescription}>{description}</p>
    </div>
  </li>
);

export function QuotePagePersonalised() {
  const [selectedOption, setSelectedOption] = useState<QuoteCoverOption | null>(null);
  const selectedOptionRef = useRef<QuoteCoverOption | null>(null);
  const [showSelectionError, setShowSelectionError] = useState(false);
  const choiceHeadingId = useId();
  const quote = useQuote();
  const quoteData = quote?.data as QuoteResponse;
  const formData = useApplianceFormData();
  const confirmation = useConfirmation();
  const actions = useQuotePageActions(quoteData);

  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const termsUrl = import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH;
  const warrantyMonths = confirmation?.applianceDetails?.guarantee ?? DEFAULT_WARRANTY_MONTHS;
  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    !!useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  const content = getPersonalisedQuoteContent({
    applianceCode: formData?.[StepKey.APPLIANCE_DETAILS]?.[FormKey.ITEM_CATEGORY],
    applianceName: formApplianceName.get(),
    brandCode: import.meta.env.VITE_BRAND_CODE,
  });
  const pricing = getPersonalisedQuotePricing(
    getDiscountDisplayData(quoteData),
    show2MonthsPromotion,
  );
  const hasSelectionError = showSelectionError && !selectedOption;

  const selectOption = (option: QuoteCoverOption) => {
    if (selectedOptionRef.current === option) return;
    selectedOptionRef.current = option;
    setSelectedOption(option);
    actions.trackOptionSelected(option);
  };

  const handleContinue = () => {
    if (!selectedOption) {
      setShowSelectionError(true);
      actions.trackSelectionValidationFailed();
      smoothScrollIntoView(0);
      return;
    }

    actions.continueWithSelection(selectedOption);
  };

  const cardState = (option: QuoteCoverOption) => {
    if (hasSelectionError) return styles.cardError;
    return selectedOption === option ? styles.cardSelected : styles.cardDefault;
  };

  const ctaLabel = {
    'manufacturer-warranty': copy.ctaWarranty,
    'appliance-care': copy.ctaApplianceCare,
  };

  return (
    <>
      <div className={styles.container} data-testid="quote-page-personalised">
        <div className={styles.panel}>
          <header className={styles.header}>
            <h1 className={styles.heading}>{copy.header}</h1>
            <p className={styles.subheading} data-testid="quote-subcopy">
              <strong>{brand} Appliance Care</strong> gives you unlimited covered repairs with no
              hidden fees, and if your {content.applianceType} can't be fixed, we'll replace it.
            </p>
          </header>

          <div className={styles.options}>
            {hasSelectionError ? (
              <div role="alert">
                <h2 id={choiceHeadingId} className={styles.optionsHeadingError}>
                  {copy.choiceError}
                </h2>
              </div>
            ) : (
              <h2 id={choiceHeadingId} className={styles.optionsHeading}>
                {copy.choice}
              </h2>
            )}

            <section
              className={styles.cards}
              role="radiogroup"
              aria-labelledby={choiceHeadingId}
              aria-invalid={hasSelectionError || undefined}
            >
              <article
                className={`${styles.card} ${styles.warrantyCard} ${cardState('manufacturer-warranty')}`}
                onClick={() => selectOption('manufacturer-warranty')}
              >
                <div className={styles.cardTitleRow}>
                  <QuoteRadioButton
                    checked={selectedOption === 'manufacturer-warranty'}
                    onChange={() => selectOption('manufacturer-warranty')}
                    name="cover-option"
                    value="manufacturer-warranty"
                    aria-label="Select Manufacturer's Warranty Only"
                  />
                  <h3 className={styles.cardTitle}>{copy.warrantyHeader}</h3>
                </div>
                <div className={styles.cardSection}>
                  <div className={styles.benefit}>
                    <BenefitIcon />
                    <p className={styles.benefitDescription} data-testid="warranty-subcopy">
                      For <strong>{warrantyMonths} months</strong> from your appliance's arrival
                      date, you'll get breakdown coverage for{' '}
                      <strong>manufacturing defects only</strong>
                    </p>
                  </div>
                  <div className={styles.indented}>
                    <p
                      className={`${styles.warrantyBox} ${selectedOption === 'manufacturer-warranty' ? styles.warrantyBoxSelected : styles.warrantyBoxDefault}`}
                      data-testid="warranty-box"
                    >
                      You may have to pay{' '}
                      {content.repairCost !== undefined ? (
                        <strong>
                          a repair cost of up to {wholeDollarFormatter.format(content.repairCost)}**
                        </strong>
                      ) : (
                        'a repair cost'
                      )}{' '}
                      where no manufacturing defect is found e.g. accidental damage
                    </p>
                  </div>
                </div>
              </article>

              <article
                className={`${styles.card} ${styles.applianceCareCard} ${cardState('appliance-care')}`}
                onClick={() => selectOption('appliance-care')}
              >
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderTitle}>
                    <QuoteRadioButton
                      checked={selectedOption === 'appliance-care'}
                      onChange={() => selectOption('appliance-care')}
                      name="cover-option"
                      value="appliance-care"
                      aria-label={`Select ${brand} Appliance Care`}
                    />
                    <div className={styles.cardHeaderText}>
                      <h3 className={styles.cardTitle}>{brand} Appliance Care</h3>
                      <p className={styles.cardSubtitle}>{copy.applianceCareSubcopy}</p>
                    </div>
                  </div>
                  {pricing.promoText && (
                    <div className={styles.tagWrapper}>
                      <span className={styles.tag} data-testid="appliance-care-tag">
                        <span className={styles.tagIcon} aria-hidden="true">
                          <GiftIcon size={24} color="currentColor" />
                        </span>
                        {pricing.promoText}
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles.applianceCareBody}>
                  <ul className={styles.benefitsList}>
                    <Benefit
                      title={`A replacement ${content.applianceType} if we can't fix it`}
                      description={copy.replacementBody}
                    />
                    <Benefit title={copy.repairTitle} description={content.repairBody} />
                    <Benefit title={copy.unlimitedTitle} description={copy.unlimitedBody} />
                    <Benefit title={copy.ongoingTitle} description={copy.ongoingBody} />
                  </ul>

                  <div className={styles.pricing}>
                    {pricing.promoText && (
                      <p className={styles.promo} data-testid="appliance-care-promo">
                        {pricing.promoText}
                      </p>
                    )}
                    <p className={styles.price} data-testid="quote-price">
                      {pricing.basePrice !== null && (
                        <span className={styles.priceBeforeDiscount}>
                          {currencyFormatter.format(pricing.basePrice)}
                        </span>
                      )}
                      {currencyFormatter.format(pricing.monthlyPrice)} a month
                    </p>
                  </div>
                </div>
              </article>
            </section>
          </div>

          <p className={styles.trust} data-testid="quote-trust">
            <span className={styles.trustIcon} aria-hidden="true">
              <ShieldTickIcon />
            </span>
            {brand} partners with Domestic & General, who protect over 22 million appliances
            worldwide
          </p>

          <div className={styles.footer}>
            <p className={styles.limitations} data-testid="quote-limitations">
              {pricing.showAchNote && '*Discount applies to payments made via bank account. '}
              Credit card payment also available. Taxes calculated at checkout. Limitations and
              exclusions apply. Please see the complete{' '}
              <a className={styles.link} href={termsUrl} target="_blank" rel="noopener noreferrer">
                {brand} Appliance Care terms and conditions
              </a>{' '}
              for details.
              {content.repairEstimatesUrl && (
                <>
                  {' '}
                  **See our average out of warranty repair estimates{' '}
                  <a
                    className={styles.link}
                    href={content.repairEstimatesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </>
              )}
            </p>

            <section className={styles.actionRow}>
              <TextLink onClick={actions.goBack} leftIcon={<ArrowLeft />}>
                {copy.back}
              </TextLink>
              <div className={styles.buttonWrapper}>
                <Button onClick={handleContinue}>
                  {selectedOption ? ctaLabel[selectedOption] : copy.cta}
                </Button>
              </div>
            </section>
          </div>
        </div>
      </div>

      <PersonalisedUpsellModal
        isOpen={actions.isUpsellModalOpen}
        onClose={actions.closeUpsellModal}
        onRegisterOnly={actions.registerOnly}
        onContinueToCheckout={actions.continueToCheckout}
        brand={brand}
        applianceType={content.applianceType}
        repairCost={content.repairCost}
        monthlyPrice={pricing.monthlyPrice}
        promoText={pricing.promoText}
      />

      <CallUsModal blocked={actions.isUpsellModalOpen} quoteId={quoteData.offer.offerReference} />
    </>
  );
}
